import WMTSCapabilities from 'ol/format/WMTSCapabilities'

import type { ConfigLayerCapabilityOptions } from './ConfigLayerCapability'
import type { ConfigLayerWMTSOptions } from './ConfigLayerWMTS'
import type { ServerType } from './ServerType'
import type { ConfigLayerOptions } from '../ConfigLayer'

import * as PoziUtils from 'api/utils'

import { ConfigLayerCapability } from './ConfigLayerCapability'
import { ConfigLayerCatalog } from './ConfigLayerCatalog'
import { ConfigLayerWMTS } from './ConfigLayerWMTS'
import { ConfigLayerType } from '../ConfigLayerType'

export interface ConfigLayerCapabilityWMTSOptions extends ConfigLayerOptions {
  type: ConfigLayerType.WMTS_CAPABILITIES
  serverType?: ServerType
  layers: ConfigLayerWMTSOptions[]
}

export class ConfigLayerCapabilityWMTS extends ConfigLayerCatalog {
  static async fetchCapability (capabilityOptions: ConfigLayerCapabilityOptions) {
    const url = PoziUtils.getLayerUrl(capabilityOptions)

    if (url) {
      try {
        const text = await ConfigLayerCapability.fetchUrl(url)
        if (text) {
          const wmtsCapability = this.getCapabilities(text)
          if (wmtsCapability) {
            const configLayerCapabilityWMTSOptions: ConfigLayerCapabilityWMTSOptions = {
              type: ConfigLayerType.WMTS_CAPABILITIES,
              serverType: capabilityOptions.serverType,
              title: capabilityOptions.title,
              titleid: capabilityOptions.titleid,
              group: capabilityOptions.group,
              groupid: capabilityOptions.groupid,
              layers: this.getLayers(capabilityOptions, wmtsCapability)
            }
            return configLayerCapabilityWMTSOptions
          }
        }
      } catch (error) {
        console.error('Error fetching capabilities: ', url, error)
      }
    } else {
      console.error('Error getting url property from config to request WMTS GetCapabilities: ', capabilityOptions)
    }
  }

  private static getCapabilities (text: string) {
    const parser = new WMTSCapabilities()
    const wmtsCapability: WMTSCapability = parser.read(text)
    // console.debug('WMTS Capabilities: ', wmtsCapability)

    return wmtsCapability
  }

  private static getLayers (capabilityOptions: ConfigLayerCapabilityOptions, wmtsCapability: WMTSCapability) {
    const layers = wmtsCapability.Contents?.Layer || []
    // const configObjs = ConfigLayerCapabilityWMTS.getChildLayers(capabilityOptions, wmtsCapability, layers)
    let configObjs: ConfigLayerWMTSOptions[] = []

    // For layer filtering below
    const hasLayerFilter = this.hasLayerFilter(capabilityOptions)
    const layerFilterArray = this.getLayerFilterArray(capabilityOptions)

    // Keep track of layers that did not match filter and report them
    const unMatchedLayers: string[] = []

    layers.forEach((layer, index: number) => {
      const configLayer = ConfigLayerWMTS.getLayer(capabilityOptions, wmtsCapability, layer, index)

      // Do not add layers marked as `enabled: false` (usually from keywords)
      const layerEnabled = configLayer.enabled ?? true
      if (!layerEnabled) {
        console.debug(`ConfigLayerCapabilityWMTS - getLayers - layer is not enabled: '${configLayer.titleid}' (${configLayer.type}). Not adding.`)
        return
      }

      // Determine whether this layer matches any provided layer filter and ignore if
      // there is no match
      if (hasLayerFilter) {
        const isMatch = this.layerMatchesFilter(configLayer, layerFilterArray)
        if (!isMatch) {
          unMatchedLayers.push(configLayer.title)

          return
        }
      }

      configObjs.push(configLayer)
    })

    if (unMatchedLayers.length > 0) {
      console.debug(`ConfigLayerCapabilityWMTS getLayers - ${capabilityOptions.titleid} - ${unMatchedLayers.length} layers did not match filter '${capabilityOptions.layerFilter}'`, unMatchedLayers)
    }

    configObjs = this.postProcessLayers(capabilityOptions, configObjs)

    // console.debug('WMTS getLayers - configObjs', configObjs)

    return configObjs
  }
}

export interface WMTSLayer {
  Abstract: string
  Format: string[]
  Identifier: string
  Style: any[]
  TileMatrixSetLink: any[]
  Title: string
  WGS84BoundingBox: number[]
  Layer?: Array<WMTSLayer>
}

export interface WMTSCapability {
  ServiceIdentification: {
    Abstract: string
    Title: string
    ServiceType: string
    ServiceTypeVersion: string
    Fees: string
    AccessConstraints: string
  }
  ServiceProvider: {
    ProviderName: string
    ProviderSite: string
  }
  Contents: {
    Layer: Array<WMTSLayer>
  }
}
